import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';

import { Checkbox, FormControlLabel, Radio, RadioGroup, Theme } from '@material-ui/core';
import { createStyles, withStyles, WithStyles } from '@material-ui/core/styles';
import { RolloutEnum } from '@lib/resources/types';
import { get } from 'lodash';
import usePermissions from 'hooks/usePermissions';

import Button from './Button';
import Dialog, { DialogProps } from './Dialog';
import DialogActions from './DialogActions';
import DialogContent from './DialogContent';
import DialogTitle from './DialogTitle';
import Typography from './Typography';

const styles = ({ spacing: { unit } }: Theme) =>
  createStyles({
    settingsGroup: {
      display: 'flex',
      flexDirection: 'column',
    },
    radio: {
      fontSize: 15,
    },
    padding: {
      paddingBottom: unit,
    },
    rowMargin: {
      marginTop: unit * 2,
    },
  });

const messages = defineMessages({
  title: {
    id: 'components.v2.DialogFormDisplaySettings.title',
    defaultMessage: 'Form Display Settings',
  },
  titleLumabot: {
    id: 'components.v2.DialogFormDisplaySettings.titleLumabot',
    defaultMessage: 'Flow Display Settings',
  },
  language: {
    id: 'components.v2.DialogFormDisplaySettings.language',
    defaultMessage: 'Displayed Language',
  },
  languageEsEn: {
    id: 'components.v2.DialogFormDisplaySettings.languageEsEn',
    defaultMessage: 'English & Spanish',
  },
  languageEn: {
    id: 'components.v2.DialogFormDisplaySettings.languageEn',
    defaultMessage: 'English',
  },
  languageEs: {
    id: 'components.v2.DialogFormDisplaySettings.languageEs',
    defaultMessage: 'Spanish',
  },
  questionVisibility: {
    id: 'components.v2.DialogFormDisplaySettings.questionVisibility',
    defaultMessage: 'Question Visibility',
  },
  unseen: {
    id: 'components.v2.DialogFormDisplaySettings.unseen',
    defaultMessage: 'Hide all questions patient didn’t see',
  },
  unanswered: {
    id: 'components.v2.DialogFormDisplaySettings.unanswered',
    defaultMessage: 'Hide all questions patient didn’t answer',
  },
  cancel: {
    id: 'components.v2.DialogFormDisplaySettings.cancel',
    defaultMessage: 'Cancel',
  },
  submit: {
    id: 'components.v2.DialogFormDisplaySettings.submit',
    defaultMessage: 'Save',
  },
});

export type InitialSettings = {
  language: 'es-en' | 'en' | 'es';
  hideUnseen: boolean;
  hideUnanswered: boolean;
};

interface DialogFormDisplaySettingsProps
  extends Omit<DialogProps, 'classes' | 'onClose' | 'onSubmit' | 'open'>,
    WithStyles<typeof styles> {
  initialSettings?: InitialSettings;
  hideLanguageSetting?: boolean;
  isLumabot?: boolean;
  onClose: () => void;
  onSubmit: (settings: any) => void;
}

const DialogFormDisplaySettings: React.FC<DialogFormDisplaySettingsProps> = ({
  classes,
  initialSettings,
  hideLanguageSetting,
  isLumabot,
  onClose,
  onSubmit,
  ...rest
}) => {
  const { formatMessage } = useIntl();

  const { hasAccessToProduct } = usePermissions();
  const hasAccessToNewPDF = hasAccessToProduct([RolloutEnum.PatientFormsNewPdf]);

  const [settings, setSettings] = useState({
    language: get(initialSettings, 'language', 'en'),
    hideUnseen: get(initialSettings, 'hideUnseen', false),
    hideUnanswered: get(initialSettings, 'hideUnanswered', false),
  });

  const handleSettingsChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, value: string) => {
      const { name } = e.target;
      setSettings({
        ...settings,
        [name]: value,
      });
    },
    [settings],
  );

  const handleOnSubmit = useCallback(() => {
    onSubmit(settings);
    onClose();
  }, [onClose, onSubmit, settings]);

  return (
    <Dialog fullWidth onClose={onClose} open {...rest}>
      <DialogTitle>{formatMessage(isLumabot ? messages.titleLumabot : messages.title)}</DialogTitle>
      <DialogContent>
        {!hideLanguageSetting && (
          <>
            <Typography variant="pSemiBold">{formatMessage(messages.language)}</Typography>
            <RadioGroup
              className={classes.settingsGroup}
              name="language"
              aria-label="Language"
              value={settings.language}
              onChange={handleSettingsChange as any}
              row
            >
              {!hasAccessToNewPDF && (
                <FormControlLabel
                  value="es-en"
                  control={<Radio classes={{ root: classes.padding }} color="primary" />}
                  label={formatMessage(messages.languageEsEn)}
                />
              )}
              <FormControlLabel
                value="en"
                control={<Radio classes={{ root: classes.padding }} color="primary" />}
                label={formatMessage(messages.languageEn)}
              />
              <FormControlLabel
                value="es"
                control={<Radio classes={{ root: classes.padding }} color="primary" />}
                label={formatMessage(messages.languageEs)}
              />
            </RadioGroup>
            <div className={classes.rowMargin} />
          </>
        )}
        <div className={classes.settingsGroup}>
          <Typography variant="pSemiBold">{formatMessage(messages.questionVisibility)}</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.hideUnseen}
                name="hideUnseen"
                onChange={handleSettingsChange as any}
                value="unseen"
                color="primary"
              />
            }
            label={formatMessage(messages.unseen)}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.hideUnanswered}
                name="hideUnanswered"
                onChange={handleSettingsChange as any}
                value="unanswered"
                color="primary"
              />
            }
            label={formatMessage(messages.unanswered)}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onClose}>
          {formatMessage(messages.cancel)}
        </Button>
        <Button variant="contained" color="primary" onClick={handleOnSubmit}>
          {formatMessage(messages.submit)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default withStyles(styles)(DialogFormDisplaySettings);
